// User Type Detection and Context-Aware Field Mapping Utilities
// This file provides safe utilities for determining user types and mapping fields
// without touching existing functionality

import Logger from '../services/logger';
import { normalizeProductUserType } from './portalAccessUtils';
import { COMPANY_USER_FIRESTORE_USER_TYPES } from '../constants/companyUserFirestoreSecurity';

export interface RecipientInfo {
  recipientId: string;
  recipientName: string;
  recipientType: 'shipper' | 'broker';
}

export interface LoadContext {
  poNumber: string;
  userType: 'shipper' | 'broker' | 'carrier';
  recipientInfo: RecipientInfo;
}

export type LoginUserType = 'shipper' | 'broker' | 'carrier' | 'unknown';

/**
 * Safely determines user type from user object
 * Falls back to default if user type is unclear
 */
export const getUserType = (user: any): 'shipper' | 'broker' | 'carrier' => {
  if (!user) return 'shipper'; // Safe default
  
  // Check explicit userType field first
  if (user.userType && ['shipper', 'broker', 'carrier', 'broker_carrier'].includes(user.userType)) {
    return user.userType === 'broker_carrier' ? 'broker' : user.userType;
  }
  
  // Check role field as fallback
  if (user.role === 'broker') return 'broker';
  if (user.role === 'carrier') return 'carrier';
  
  // Check companyUserRole as additional fallback
  if (user.companyUserRole === 'company_owner') return 'broker';
  if (user.companyUserRole === 'driver') return 'carrier';
  
  // Check for carrier operations
  if (user.hasCarrierOperations) return 'broker';
  
  // Default to shipper for safety
  return 'shipper';
};

/**
 * Login-only user type resolution — no shipper default fallback.
 * Requires explicit userType or strong carrier/broker indicators.
 */
export const determineUserTypeForLogin = (userData: any): {
  userType: LoginUserType;
  confidence: 'high' | 'medium' | 'low';
  source: string;
} => {
  if (!userData) {
    return { userType: 'unknown', confidence: 'low', source: 'undetermined' };
  }

  const ut = normalizeProductUserType(userData.userType);
  if (
    ut === COMPANY_USER_FIRESTORE_USER_TYPES.SHIPPER ||
    ut === COMPANY_USER_FIRESTORE_USER_TYPES.BROKER ||
    ut === COMPANY_USER_FIRESTORE_USER_TYPES.CARRIER ||
    ut === COMPANY_USER_FIRESTORE_USER_TYPES.BROKER_CARRIER
  ) {
    const userType: LoginUserType =
      ut === COMPANY_USER_FIRESTORE_USER_TYPES.SHIPPER
        ? 'shipper'
        : ut === COMPANY_USER_FIRESTORE_USER_TYPES.CARRIER
          ? 'carrier'
          : 'broker';
    return { userType, confidence: 'high', source: 'userType field' };
  }

  if (userData.mcNumber || userData.dotNumber || userData.eldApiKey) {
    return { userType: 'carrier', confidence: 'medium', source: 'carrier-specific fields' };
  }

  if (userData.hasCarrierOperations === true) {
    return { userType: 'broker', confidence: 'medium', source: 'hasCarrierOperations field' };
  }

  if (userData.role === 'broker') {
    return { userType: 'broker', confidence: 'medium', source: 'role field' };
  }

  if (userData.role === 'carrier' || userData.companyUserRole === 'driver') {
    return { userType: 'carrier', confidence: 'medium', source: 'role or companyUserRole field' };
  }

  return { userType: 'unknown', confidence: 'low', source: 'undetermined' };
};

/**
 * Gets recipient information based on load data
 * Determines recipient by checking load data directly (brokerId or shipperId)
 * Handles both shipper and broker scenarios safely
 */
export const getRecipientInfo = (load: any, userType?: string): RecipientInfo => {
  if (!load) {
    throw new Error('Load data is required to determine recipient');
  }

  // Check load data directly to determine recipient type
  // Priority: brokerId > shipperId > userId (fallback)
  if (load.brokerId) {
    // Broker load - recipient is broker
    return {
      recipientId: load.brokerId,
      recipientName: load.brokerName || load.companyInfo?.name || 'Broker',
      recipientType: 'broker'
    };
  } else if (load.shipperId) {
    // Shipper load - recipient is shipper
    return {
      recipientId: load.shipperId,
      recipientName: load.shipperName || load.companyInfo?.name || 'Shipper',
      recipientType: 'shipper'
    };
  } else if (load.userId) {
    // Fallback: check userId (could be either broker or shipper)
    // Try to determine from load context
    if (userType === 'broker' || load.brokerName) {
      return {
        recipientId: load.userId,
        recipientName: load.brokerName || 'Broker',
        recipientType: 'broker'
      };
    } else {
      return {
        recipientId: load.userId,
        recipientName: load.shipperName || 'Shipper',
        recipientType: 'shipper'
      };
    }
  } else {
    // No recipient ID found
    throw new Error('Unable to determine recipient: load missing brokerId, shipperId, and userId');
  }
};

/**
 * Gets complete load context including user type and recipient info
 * Safe to use for determining workflow paths
 */
export const getLoadContext = (load: any, user: any): LoadContext => {
  const userType = getUserType(user);
  const recipientInfo = getRecipientInfo(load, userType);
  
  return {
    poNumber: load.poNumber || '',
    userType,
    recipientInfo
  };
};

/**
 * Determines which collection to use based on user type
 * Ensures proper collection separation
 */
export const getCollectionByUserType = (userType: string, baseCollection: string): string => {
  if (userType === 'broker') {
    return `broker${baseCollection.charAt(0).toUpperCase() + baseCollection.slice(1)}`;
  }
  return baseCollection;
};

/**
 * Gets the appropriate route based on user type
 * Maintains existing routing behavior
 */
export const getDefaultRoute = (userType: string): string => {
  switch(userType) {
    case 'broker':
      return '/shipper/dashboard'; // Brokers use shipper portal
    case 'shipper':
      return '/shipper/dashboard';
    case 'carrier':
      return '/carrier/home';
    default:
      return '/shipper/dashboard'; // Safe default
  }
};

/**
 * Validates that a load belongs to the correct user type
 * Prevents cross-user-type access
 */
export const validateLoadAccess = (load: any, user: any): boolean => {
  const userType = getUserType(user);
  
  if (userType === 'broker') {
    // Brokers should only access broker loads
    return !!(load.brokerId || load.brokerPurchaseOrderId);
  } else if (userType === 'shipper') {
    // Shippers should only access shipper loads
    return !!(load.shipperId || load.userId);
  }
  
  return true; // Carriers can access both
};
